import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Typography,
  Checkbox,
  FormControlLabel,
  Grid,
  Divider,
} from "@mui/material";
import { getSampleCollectionsFromBaseTable } from "../../../services/labService";
import { getAllLabTests } from "../../../services/labtestService";
import { updateSampleCollection } from "../../../services/PatientlabTestService";
import { getIpdLabTestStatus, normalizeTestName, filterTestsBySelectedSamples, extractLabTestSampleIds, findCatalogLabTest } from "../labTestWorkflowUtils";
import { recordSamplesCollected } from "../../../services/sampleTransactionService";
import { buildSampleCollectedId } from "../../../utils/sampleTransactionUtils";
import {
  usePathBomCollectInventoryFlow,
  buildSampleCollectSuccessMessage,
} from "../../../hooks/usePathBomCollectInventoryFlow";

const IpdCollectSamplePanel = ({ group, onSampleCollected, embedded = false, onProcessingChange }) => {
  const [samples, setSamples] = useState([]);
  const [selectedSamples, setSelectedSamples] = useState([]);
  const [loading, setLoading] = useState(false);
  const { inventoryDialogs, gateReviewAndDeduct } = usePathBomCollectInventoryFlow();

  const pendingTests = (group?.labTests || []).filter(
    (test) => getIpdLabTestStatus(test) === "PENDING"
  );

  const sampleIds = samples.map((sample) => sample.id);
  const allSamplesSelected = sampleIds.length > 0 && sampleIds.every((id) => selectedSamples.includes(id));
  const someSamplesSelected = selectedSamples.length > 0 && !allSamplesSelected;

  const handleSelectAllSamples = (checked) => {
    setSelectedSamples(checked ? sampleIds : []);
  };

  useEffect(() => {
    const fetchSamples = async () => {
      if (!group || pendingTests.length === 0) {
        setSamples([]);
        return;
      }

      try {
        const allLabTests = await getAllLabTests();
        const testNames = pendingTests
          .map((test) => normalizeTestName(test.resolvedTestName || test.testName))
          .filter(Boolean);

        const matchingTests = allLabTests.filter((labTest) => {
          const labTestName = normalizeTestName(
            labTest.testName || labTest.name || labTest.test_name || labTest.labTestName
          );
          const labTestId = labTest.id;
          return (
            testNames.includes(labTestName) ||
            pendingTests.some((test) => Number(test.testId || test.labTestId) === Number(labTestId))
          );
        });

        const sampleIds = Array.from(
          new Set(
            matchingTests
              .flatMap((matchingTest) => {
                const ids = [];
                if (Array.isArray(matchingTest.sampleEntries) && matchingTest.sampleEntries.length) {
                  matchingTest.sampleEntries.forEach((entry) => {
                    const value = entry?.sample_id ?? entry?.sampleId;
                    if (value != null && value !== "") ids.push(Number(value));
                  });
                }
                if (matchingTest.sampleId) {
                  String(matchingTest.sampleId)
                    .split(",")
                    .map((part) => part.trim())
                    .filter(Boolean)
                    .forEach((part) => ids.push(Number(part)));
                }
                return ids;
              })
              .filter((id) => !Number.isNaN(id))
          )
        );

        const allSamples = await getSampleCollectionsFromBaseTable();
        const filteredSamples = allSamples.filter((sample) => sampleIds.includes(sample.id));
        const samplesWithTests = filteredSamples.map((sample) => {
          const associatedTestNames = pendingTests
            .filter((patientTest) =>
              extractLabTestSampleIds(findCatalogLabTest(patientTest, matchingTests)).includes(
                Number(sample.id)
              )
            )
            .map((patientTest) => patientTest.resolvedTestName || patientTest.testName)
            .filter(Boolean);
          return { ...sample, associatedTestNames };
        });
        setSamples(samplesWithTests);
        setSelectedSamples([]);
      } catch (error) {
        console.error("[IpdCollectSamplePanel] Error fetching samples:", error);
        setSamples([]);
      }
    };

    fetchSamples();
  }, [group?.key, pendingTests.map((test) => test.id).join("|")]);

  const handleCollect = async () => {
    if (selectedSamples.length === 0) {
      alert("Please select at least one sample to collect.");
      return;
    }

    setLoading(true);
    onProcessingChange && onProcessingChange(true);
    try {
      const allLabTests = await getAllLabTests();
      const testsToCollect = filterTestsBySelectedSamples(
        pendingTests,
        allLabTests,
        selectedSamples
      );
      if (testsToCollect.length === 0) {
        alert("Selected sample is not associated with a pending lab test. Select the sample(s) for the test you want to collect.");
        return;
      }

      const testsForInventory = [];
      const patientContextByLabTestId = {};
      for (const test of testsToCollect) {
        if (!test.id) continue;
        if (test.assignedBy === null) continue;
        const catalog = findCatalogLabTest(test, allLabTests);
        const catalogId = Number(
          catalog?.id || test.testId || test.labTestId
        );
        if (!Number.isFinite(catalogId) || catalogId <= 0) continue;
        testsForInventory.push({
          ...test,
          labTestId: catalogId,
          testId: catalogId,
          testName: test.resolvedTestName || test.testName,
          resolvedTestName: test.resolvedTestName || test.testName,
        });
        patientContextByLabTestId[catalogId] = {
          labTestName: test.resolvedTestName || test.testName,
          patientId: test.patientId ?? group?.patientId,
          patientName: test.patientName ?? group?.patientName,
          patientType: "ipd",
          doctorName:
            test.resolvedDoctorName ||
            test.doctorName ||
            test.assignedByName ||
            group?.doctorName,
          doctorId: test.assignedBy ?? test.doctorId ?? test.doctor_id,
          prescriptionId:
            test.prescriptionId ??
            test.ipdPrescriptionId ??
            test.ipd_prescription_id ??
            group?.prescriptionId,
          invoiceId: test.invoiceId ?? test.invoice_id ?? group?.invoiceId,
          admissionId: test.admissionId ?? group?.admissionId,
          reportId: test.reportId ?? test.report_id ?? null,
        };
      }

      const inventoryGate = await gateReviewAndDeduct({
        patientLabTests: testsForInventory,
        patientContextByLabTestId,
        catalogLabTests: allLabTests,
      });
      if (!inventoryGate.ok) {
        if (inventoryGate.reason === "cancelled") return;
        if (inventoryGate.reason !== "insufficient_stock") {
          alert(
            inventoryGate.errorMessage ||
              "Failed to deduct pathology inventory. Sample was not collected."
          );
        }
        return;
      }

      const collectedTests = [];
      for (const test of testsToCollect) {
        if (!test.id) continue;
        if (test.assignedBy === null) {
          console.warn("[IpdCollectSamplePanel] Skipping test without assignedBy:", test.id);
          continue;
        }
        await updateSampleCollection(test.id);
        collectedTests.push(test);
      }

      const sampleRows = collectedTests.map((test) => ({
        prescriptionId:
          test.prescriptionId ??
          test.ipdPrescriptionId ??
          test.ipd_prescription_id ??
          group?.prescriptionId,
        patientId: test.patientId ?? group?.patientId,
        patientName: test.patientName ?? group?.patientName,
        labTestId: test.testId || test.labTestId,
        labTestName: test.resolvedTestName || test.testName,
        billed: Boolean(test.invoiceId || test.invoice_id || group?.invoiceId),
        invoiceId: test.invoiceId ?? test.invoice_id ?? group?.invoiceId,
        admissionId: test.admissionId ?? group?.admissionId,
        reportId: test.reportId ?? test.report_id ?? null,
        patientType: "ipd",
        sampleCollected: true,
        result: false,
      }));
      const { uniqueSampleCollectedIds } = await recordSamplesCollected(sampleRows);
      const displayIds =
        uniqueSampleCollectedIds.length > 0
          ? uniqueSampleCollectedIds
          : sampleRows.filter((row) => row.patientId && row.labTestId).map((row) => buildSampleCollectedId(row));
      alert(
        buildSampleCollectSuccessMessage({
          deducted: Boolean(inventoryGate.deducted),
          testNames: collectedTests.map(
            (test) => test.resolvedTestName || test.testName
          ),
          sampleIds: displayIds,
        })
      );
      onSampleCollected && onSampleCollected();
    } catch (error) {
      console.error("[IpdCollectSamplePanel] Failed to collect samples:", error);
      alert("Failed to collect samples. Please try again.");
    } finally {
      setLoading(false);
      onProcessingChange && onProcessingChange(false);
    }
  };

  if (pendingTests.length === 0) return null;

  const stockDialog = inventoryDialogs;

  const content = (
    <>
      {!embedded && (
        <>
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
            Collect Samples
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Pending tests: {pendingTests.map((test) => test.resolvedTestName || test.testName).join(", ")}
          </Typography>
        </>
      )}
      {embedded && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Pending tests: {pendingTests.map((test) => test.resolvedTestName || test.testName).join(", ")}
        </Typography>
      )}
      <Grid container spacing={1}>
        {samples.length > 0 && (
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={allSamplesSelected}
                  indeterminate={someSamplesSelected}
                  onChange={(event) => handleSelectAllSamples(event.target.checked)}
                  disabled={loading}
                />
              }
              label="Select All"
            />
          </Grid>
        )}
        {samples.length > 0 ? (
          samples.map((sample) => (
            <Grid item xs={12} key={sample.id}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={selectedSamples.includes(sample.id)}
                    onChange={() => {
                      setSelectedSamples((prev) =>
                        prev.includes(sample.id)
                          ? prev.filter((id) => id !== sample.id)
                          : [...prev, sample.id]
                      );
                    }}
                    disabled={loading}
                  />
                }
                label={`${sample.name} (${sample.unitType}) - Required: ${sample.requiredQuantity}${
                  Array.isArray(sample.associatedTestNames) && sample.associatedTestNames.length
                    ? ` — ${sample.associatedTestNames.join(", ")}`
                    : ""
                }`}
              />
            </Grid>
          ))
        ) : (
          <Grid item xs={12}>
            <Typography variant="body2" color="text.secondary">
              Samples are not configured for these tests.
            </Typography>
          </Grid>
        )}
      </Grid>
      <Divider sx={{ my: 1.5 }} />
      <Box display="flex" justifyContent="flex-end">
        <Button
          variant="contained"
          color="secondary"
          onClick={handleCollect}
          disabled={loading || selectedSamples.length === 0}
        >
          {loading ? "Processing..." : "Collect"}
        </Button>
      </Box>
    </>
  );

  if (embedded) {
    return (
      <>
        {stockDialog}
        {content}
      </>
    );
  }

  return (
    <>
      {stockDialog}
      <Box sx={{ mt: 2, p: 2, border: "1px solid #e0e0e0", borderRadius: 1, bgcolor: "#fafafa" }}>
        {content}
      </Box>
    </>
  );
};

export default IpdCollectSamplePanel;
